import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {toast} from 'react-toastify';
import {ResourceAllocationState, Person} from '../../../types';
import {removePersonCascade} from '../cascadeDelete';

// Billing caps come back as a 400 with a machine-readable prefix. Without this
// the optimistic revert is the only feedback — the grant just silently undoes
// itself, which reads as a broken control.
const notifyIfCapExceeded = (errorText: string) => {
    if (errorText.includes('maintainer_seat_cap_exceeded')) {
        toast.error(
            'All maintainer seats are in use. Free one up, or add seats on the billing page, then try again.'
        );
    } else if (errorText.includes('resource_cap_exceeded')) {
        toast.error(
            'You have reached your tracked-people limit. Add a resource block on the billing page to track more.'
        );
    }
};

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export const usePersonActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    const addPerson = useCallback(
        async (person: Omit<Person, 'id'>): Promise<string> => {
            const id = uuidv4();
            const newPerson = {
                ...person,
                id,
                customerRoles: person.customerRoles || [] // Ensure array exists
            };

            // Optimistic update
            setState(prev => ({
                ...prev,
                people: {...prev.people, [id]: newPerson}
            }));

            // API call — awaited so callers can sequence dependent creates and
            // surface failures (the server persists this same client-generated id).
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.people;
                        return {...prev, people: rest};
                    });

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/people`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: id, // Send client-generated ID
                                firstName: person.firstName,
                                lastName: person.lastName,
                                email: person.email,
                                phoneNumber: person.phoneNumber,
                                jobTitle: person.jobTitle,
                                workerType: person.workerType,
                                teamIds: person.teamIds || [],
                                roleIds: person.roleIds || [],
                                customerRoles: person.customerRoles || []
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(
                            '[addPerson] API call failed:',
                            errorText
                        );
                        revert();
                        throw new Error(
                            `Failed to create person: ${errorText}`
                        );
                    }

                    // Pick up server-computed fields the client can't derive —
                    // notably emailLocked (whether the email linked to a verified
                    // user account) and the normalised email.
                    try {
                        const created = await response.json();
                        if (created) {
                            setState(prev => ({
                                ...prev,
                                people: {
                                    ...prev.people,
                                    [id]: {
                                        ...prev.people[id],
                                        email: created.email ?? null,
                                        emailLocked:
                                            created.emailLocked ?? false
                                    }
                                }
                            }));
                        }
                    } catch {
                        // No/!JSON body — optimistic state stands.
                    }
                } catch (error: unknown) {
                    console.error('[addPerson] API call error:', error);
                    revert();
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updatePerson = useCallback(
        (id: string, updates: Partial<Person>) => {
            const state = stateRef.current;
            // Optimistic update. Under scoped state loading the person may not be
            // in the map (e.g. picked via server search) — spreading undefined
            // would fabricate a ghost entity, so only patch what's present.
            const previousPerson = state.people[id];
            if (previousPerson) {
                setState(prev => ({
                    ...prev,
                    people: {
                        ...prev.people,
                        [id]: {...prev.people[id], ...updates}
                    }
                }));
            }

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/people/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            // Revert on failure (e.g. the server rejects an edit
                            // to an email that is locked to a verified user).
                            const errorText = await response.text();
                            console.error(
                                '[updatePerson] API call failed:',
                                errorText
                            );
                            notifyIfCapExceeded(errorText);
                            if (previousPerson) {
                                setState(prev => ({
                                    ...prev,
                                    people: {
                                        ...prev.people,
                                        [id]: previousPerson
                                    }
                                }));
                            }
                            return;
                        }

                        // Sync server-computed fields the client can't derive,
                        // so the email lock reflects immediately for the editor.
                        try {
                            const updated = await response.json();
                            if (updated) {
                                setState(prev => {
                                    if (!prev.people[id]) return prev;
                                    return {
                                        ...prev,
                                        people: {
                                            ...prev.people,
                                            [id]: {
                                                ...prev.people[id],
                                                email: updated.email ?? null,
                                                emailLocked:
                                                    updated.emailLocked ?? false
                                            }
                                        }
                                    };
                                });
                            }
                        } catch {
                            // No/!JSON body — optimistic state stands.
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[updatePerson] API call error:', error);
                        // Revert on error
                        if (previousPerson) {
                            setState(prev => ({
                                ...prev,
                                people: {...prev.people, [id]: previousPerson}
                            }));
                        }
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Expire a person (the default "delete" action): they leave active selection
    // but are kept for history so past allocations still resolve. We patch the
    // record in place rather than removing it.
    const expirePerson = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousPerson = state.people[id];
            if (!previousPerson) return;
            const expiredOn = new Date().toISOString();

            setState(prev => ({
                ...prev,
                people: {
                    ...prev.people,
                    [id]: {...prev.people[id], expiredOn}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    people: {...prev.people, [id]: previousPerson}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/people/${id}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[expirePerson] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[expirePerson] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Un-expire a person — clears expiredOn so they return to active selection.
    const reinstatePerson = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousPerson = state.people[id];
            if (!previousPerson) return;

            setState(prev => ({
                ...prev,
                people: {
                    ...prev.people,
                    [id]: {...prev.people[id], expiredOn: null}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    people: {...prev.people, [id]: previousPerson}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/people/${id}/reinstate`,
                    {
                        method: 'POST',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[reinstatePerson] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[reinstatePerson] API call error:',
                            error
                        );
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Permanently delete a person (created-in-error escape hatch). Full cascade
    // removal locally; rejects if the server refuses (history exists) so the
    // caller can surface why and the reverted row reappears.
    const permanentlyDeletePerson = useCallback(
        async (id: string): Promise<void> => {
            const state = stateRef.current;
            // Snapshot every collection the cascade touches, for revert on failure.
            const backup = {
                person: state.people[id],
                projectPeople: Object.fromEntries(
                    Object.entries(state.projectPeople).filter(
                        ([, pp]) => pp.personId === id
                    )
                ),
                personalAvailability: Object.fromEntries(
                    Object.entries(state.personalAvailability).filter(
                        ([, pa]) => pa.personId === id
                    )
                ),
                personalAvailabilityWeeks: Object.fromEntries(
                    Object.entries(state.personalAvailabilityWeeks).filter(
                        ([, paw]) => paw.personId === id
                    )
                )
            };

            // Optimistic update — full cascade (mirrors the DB ON DELETE CASCADE).
            setState(prev => removePersonCascade(prev, id));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    people: {...prev.people, [id]: backup.person},
                    projectPeople: {
                        ...prev.projectPeople,
                        ...backup.projectPeople
                    },
                    personalAvailability: {
                        ...prev.personalAvailability,
                        ...backup.personalAvailability
                    },
                    personalAvailabilityWeeks: {
                        ...prev.personalAvailabilityWeeks,
                        ...backup.personalAvailabilityWeeks
                    }
                }));

            if (!customerId || !authenticatedFetch) return;

            const connectionId = signalRService.getConnectionId();
            let response: Response;
            try {
                response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/people/${id}/permanent`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                );
            } catch (error: unknown) {
                console.error(
                    '[permanentlyDeletePerson] API call error:',
                    error
                );
                revert();
                throw error;
            }

            if (!response.ok) {
                const message = await response.text();
                console.error(
                    '[permanentlyDeletePerson] API call failed:',
                    message
                );
                revert();
                throw new Error(
                    message || 'Failed to permanently delete person'
                );
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Remove the link between this person and any user account(s). Frees a
    // verified-locked email to be edited again (the escape hatch for a person
    // wrongly auto-matched to the wrong user). Admin-only on the server.
    const unlinkPersonUser = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousPerson = state.people[id];
            if (!previousPerson) return;

            // Optimistic: the email is no longer locked once unlinked.
            setState(prev => ({
                ...prev,
                people: {
                    ...prev.people,
                    [id]: {...prev.people[id], emailLocked: false}
                }
            }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/people/${id}/user-link`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[unlinkPersonUser] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                people: {...prev.people, [id]: previousPerson}
                            }));
                            return;
                        }
                        try {
                            const updated = await response.json();
                            if (updated) {
                                setState(prev => ({
                                    ...prev,
                                    people: {
                                        ...prev.people,
                                        [id]: {
                                            ...prev.people[id],
                                            email: updated.email ?? null,
                                            emailLocked:
                                                updated.emailLocked ?? false
                                        }
                                    }
                                }));
                            }
                        } catch {
                            // No/!JSON body — optimistic state stands.
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[unlinkPersonUser] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            people: {...prev.people, [id]: previousPerson}
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Merge server-fetched people into state WITHOUT overwriting anything already
    // loaded (state entities are fresher). Used when a server-searched pick isn't
    // in the scoped /state yet, so optimistic renders can resolve its name until
    // the next refetch/SignalR event delivers the authoritative entity.
    const ensurePeople = useCallback(
        (people: Person[]) => {
            setState(prev => {
                const additions = people.filter(p => p && !prev.people[p.id]);
                if (additions.length === 0) return prev;
                const merged = {...prev.people};
                additions.forEach(p => {
                    merged[p.id] = p;
                });
                return {...prev, people: merged};
            });
        },
        [setState]
    );

    return useMemo(
        () => ({
            addPerson,
            updatePerson,
            ensurePeople,
            expirePerson,
            reinstatePerson,
            permanentlyDeletePerson,
            unlinkPersonUser
        }),
        [
            addPerson,
            updatePerson,
            ensurePeople,
            expirePerson,
            reinstatePerson,
            permanentlyDeletePerson,
            unlinkPersonUser
        ]
    );
};
